import React, { useContext, useMemo, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Select, Space, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import {
  categoryOptions,
  subCategoryOptions,
  typeOptions,
} from '@totem/components/ticketing/assignments/categorizationOptions';
import MatchValueInput from '@totem/components/ticketing/assignments/MatchValueInput';
import TicketAssignmentsContext from '@totem/components/ticketing/assignments/TicketAssignmentsContext';
import {
  TicketAssignmentTestRequest,
  TicketAssignmentTestResult,
} from '@totem/components/ticketing/assignments/types';
import { ASSIGNEE_TYPE_LABELS } from '@totem/components/ticketing/templateManagement/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

type TestFormValues = {
  type?: string;
  category?: string;
  subCategory?: string;
  summary?: string;
  organizationId?: string;
  creatorEmail?: string;
};

const resultStatus = (result: TicketAssignmentTestResult) => {
  if (result.applied) return <Tag color="green">Assigns ticket</Tag>;
  if (!result.active) return <Tag>Inactive</Tag>;
  if (!result.fulfilled) return <Tag color="orange">No assignee found</Tag>;
  return <Tag>Skipped (already claimed)</Tag>;
};

const TicketAssignmentTestModal = () => {
  const {
    testOpen,
    onTestClose,
    runTest,
    assignments,
    categorization,
    organizations,
  } = useContext(TicketAssignmentsContext);
  const [form] = Form.useForm<TestFormValues>();
  const [results, setResults] = useState<TicketAssignmentTestResult[] | null>(
    null,
  );
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const selectedType = Form.useWatch('type', form);
  const selectedCategory = Form.useWatch('category', form);
  const organizationOptions = useMemo(
    () =>
      [...organizations]
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((org) => ({ label: org.name, value: org.id })),
    [organizations],
  );

  const handleRun = () => {
    const values = form.getFieldsValue();
    const request: TicketAssignmentTestRequest = {
      type: values.type ?? '',
      category: values.category ?? '',
      subCategory: values.subCategory ?? '',
      summary: values.summary ?? '',
      organizationId: values.organizationId ?? '',
      buildingId: '',
      creatorEmail: (values.creatorEmail ?? '').trim().toLowerCase(),
    };

    setRunning(true);

    setError('');

    runTest(request)
      .then(setResults)
      .catch((err: Error) => setError(err.message))
      .finally(() => setRunning(false));
  };

  const handleClose = () => {
    setResults(null);

    setError('');

    onTestClose();
  };

  const columns: ColumnProps<TicketAssignmentTestResult>[] = [
    {
      title: 'Seq',
      key: 'sequence',
      width: 60,
      render: (_, result) => result.assignment.sequence,
    },
    {
      title: 'Entry',
      key: 'name',
      render: (_, result) => result.assignment.name,
    },
    {
      title: 'Result',
      key: 'result',
      width: 170,
      render: (_, result) => resultStatus(result),
    },
    {
      title: 'Would add',
      key: 'people',
      render: (_, result) =>
        result.people.length === 0 ? (
          <span>No one found</span>
        ) : (
          result.people.map((person) => (
            <div key={person.email}>
              <Tag>{person.partyType}</Tag>
              {person.name || person.email}{' '}
              <span style={{ opacity: 0.65 }}>
                (
                {ASSIGNEE_TYPE_LABELS[person.assigneeType] ??
                  person.assigneeType}
                {person.value ? `: ${person.value}` : ''})
              </span>
            </div>
          ))
        ),
    },
  ];

  return (
    <Modal
      title="Test Ticket Assignment"
      open={testOpen}
      onCancel={handleClose}
      width={900}
      footer={
        <Space>
          <Button onClick={handleClose}>Close</Button>
          <Button type="primary" loading={running} onClick={handleRun}>
            Test
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" size="small">
        <Space style={{ width: '100%' }} size="middle" align="start">
          <Form.Item name="type" label="Ticket Type" style={{ width: 200 }}>
            <MatchValueInput
              options={typeOptions(
                categorization,
                assignments.map((record) => record.match.type),
              )}
            />
          </Form.Item>
          <Form.Item name="category" label="Category" style={{ width: 200 }}>
            <MatchValueInput
              options={categoryOptions(
                categorization,
                selectedType,
                assignments.map((record) => record.match.category),
              )}
            />
          </Form.Item>
          <Form.Item
            name="subCategory"
            label="Sub-Category"
            style={{ width: 200 }}
          >
            <MatchValueInput
              options={subCategoryOptions(
                categorization,
                selectedType,
                selectedCategory,
                assignments.map((record) => record.match.subCategory),
              )}
            />
          </Form.Item>
        </Space>
        <Form.Item name="summary" label="Summary">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Space style={{ width: '100%' }} size="middle" align="start">
          <Form.Item
            name="organizationId"
            label="Organization"
            tooltip="Organization contacts and scoped entries are resolved against this organization."
            style={{ width: 360 }}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={organizationOptions}
            />
          </Form.Item>
          <Form.Item
            name="creatorEmail"
            label="Created by (user email)"
            tooltip="Leave blank to simulate a ticket created by a service or inbound email (no Ticket Creator)."
            style={{ width: 300 }}
          >
            <Input placeholder="Optional" />
          </Form.Item>
        </Space>
      </Form>

      {error && <Alert type="error" message={error} />}
      {results && (
        <IBTable
          rowKey={(result) => result.assignment.id}
          columns={columns}
          dataSource={results}
          size="small"
          pagination={false}
          locale={{ emptyText: 'No entries match this ticket.' }}
        />
      )}
    </Modal>
  );
};

export default TicketAssignmentTestModal;
